/** @jsxImportSource react */
import type { HTMLAttributes, ReactNode, JSX } from "react";
import {
  OperatorPanelParagraph,
  OperatorSummaryList,
  OperatorSummaryItem,
  OperatorSummaryMetadata,
  OperatorSummarySeparator,
  OperatorSummaryTags,
  OperatorSummaryTag,
  OperatorStatusPill,
  OperatorActionLinks,
  OperatorActionLink,
  OperatorTabStrip,
  OperatorTabButton,
  OperatorTabCount,
  OperatorChoiceGroup,
  OperatorChoiceButton,
  OperatorChoiceCount,
  OperatorChoiceLabel,
  OperatorChoiceTools,
  OperatorChoiceSearch,
  OperatorChoiceToggle,
} from "@brains/operator-view-react";

function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

export interface WidgetDataAttributes {
  [key: `data-${string}`]: string | number | boolean | undefined;
}

export type WidgetElementProps<T extends HTMLElement> = HTMLAttributes<T> &
  WidgetDataAttributes;

export function createWidgetInstanceId(
  pluginId: string,
  widgetId: string,
): string {
  const slug = `${pluginId}-${widgetId}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `widget-${slug || "custom"}`;
}

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
}

export function KeyValueList({
  items,
}: {
  items: KeyValueItem[];
}): JSX.Element {
  return (
    <dl className="kv">
      {items.map((item, index) => (
        <div className="kv-row" key={index}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function WidgetEmptyState({
  children = "Nothing to show yet.",
  className,
}: {
  children?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <OperatorPanelParagraph presentation="muted" className={className}>
      {children}
    </OperatorPanelParagraph>
  );
}

export const EmptyState: typeof WidgetEmptyState = WidgetEmptyState;

export function WidgetActions({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <OperatorActionLinks
      className={classes("widget-actions", className)}
      aria-label={label}
    >
      {children}
    </OperatorActionLinks>
  );
}

export function WidgetActionLink({
  href,
  children,
  external = false,
  emphasis = "secondary",
}: {
  href: string;
  children: ReactNode;
  external?: boolean;
  emphasis?: "primary" | "secondary";
}): JSX.Element {
  return (
    <OperatorActionLink
      className="widget-action"
      emphasis={emphasis}
      indicator={external ? "↗" : "→"}
      href={href}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {children}
    </OperatorActionLink>
  );
}

export interface WidgetTabDefinition {
  value: string;
  label: ReactNode;
  count?: number | undefined;
  content: ReactNode;
  panelClassName?: string | undefined;
  triggerProps?: WidgetElementProps<HTMLButtonElement> | undefined;
  panelProps?: WidgetElementProps<HTMLDivElement> | undefined;
}

export function WidgetTabs({
  id,
  label,
  defaultValue,
  tabs,
  variant = "line",
  compact = false,
  stateAttribute,
  rootClassName,
  rootProps,
}: {
  id: string;
  label: string;
  defaultValue: string;
  tabs: WidgetTabDefinition[];
  variant?: "line" | "pill";
  compact?: boolean;
  stateAttribute?: string;
  rootClassName?: string;
  rootProps?: WidgetElementProps<HTMLDivElement> | undefined;
}): JSX.Element {
  const listClass =
    variant === "line"
      ? "widget-tabs"
      : classes("widget-filter-tabs", compact && "widget-filter-tabs--compact");
  const triggerClass = variant === "line" ? "widget-tab" : "widget-filter-tab";
  const countClass =
    variant === "line" ? "widget-tab-count" : "widget-filter-count";
  const TabList = variant === "line" ? OperatorTabStrip : OperatorChoiceGroup;
  const TabButton =
    variant === "line" ? OperatorTabButton : OperatorChoiceButton;

  return (
    <div
      {...rootProps}
      className={rootClassName}
      data-ui-tabs
      data-ui-tabs-default={defaultValue}
      {...(stateAttribute
        ? { "data-ui-tabs-state-attribute": stateAttribute }
        : {})}
    >
      <TabList
        {...(variant === "pill" ? { compact } : {})}
        className={listClass}
        role="tablist"
        aria-label={label}
      >
        {tabs.map((tab) => {
          const active = tab.value === defaultValue;
          const triggerId = `${id}-tab-${tab.value}`;
          const panelId = `${id}-panel-${tab.value}`;
          return (
            <TabButton
              key={tab.value}
              {...tab.triggerProps}
              id={triggerId}
              className={triggerClass}
              {...(variant === "pill" ? { compact } : {})}
              type="button"
              role="tab"
              data-ui-tab={tab.value}
              aria-controls={panelId}
              aria-selected={active ? "true" : "false"}
            >
              {variant === "pill" ? (
                <>
                  {tab.count !== undefined && (
                    <OperatorChoiceCount className={countClass}>
                      {tab.count}
                    </OperatorChoiceCount>
                  )}
                  <OperatorChoiceLabel className="widget-filter-label">
                    {tab.label}
                  </OperatorChoiceLabel>
                </>
              ) : (
                <>
                  {tab.label}
                  {tab.count !== undefined && (
                    <OperatorTabCount className={countClass}>
                      {tab.count}
                    </OperatorTabCount>
                  )}
                </>
              )}
            </TabButton>
          );
        })}
      </TabList>
      {tabs.map((tab) => {
        const active = tab.value === defaultValue;
        return (
          <div
            key={tab.value}
            {...tab.panelProps}
            id={`${id}-panel-${tab.value}`}
            className={classes(tab.panelClassName, active && "is-active")}
            data-ui-panel={tab.value}
            role="tabpanel"
            aria-labelledby={`${id}-tab-${tab.value}`}
            hidden={!active}
          >
            {tab.content}
          </div>
        );
      })}
    </div>
  );
}

export interface WidgetFilterOption {
  value: string;
  label: ReactNode;
  count?: number | undefined;
  tone?: "plain" | "gap" | undefined;
  triggerProps?: WidgetElementProps<HTMLButtonElement> | undefined;
}

const WIDGET_FILTER_INITIAL_VISIBLE_OPTIONS = 12;

function filterOptionSearchText(option: WidgetFilterOption): string {
  const label =
    typeof option.label === "string" || typeof option.label === "number"
      ? String(option.label)
      : option.value;
  return label.trim().toLowerCase();
}

export function WidgetFilter({
  label,
  defaultValue,
  options,
  children,
  emptyState,
  className,
  allValue = "all",
}: {
  label: string;
  defaultValue: string;
  options: WidgetFilterOption[];
  children: ReactNode;
  emptyState?: ReactNode;
  className?: string;
  allValue?: string;
}): JSX.Element {
  const hasOverflow = options.length > WIDGET_FILTER_INITIAL_VISIBLE_OPTIONS;

  return (
    <div
      className={className}
      data-ui-filter
      data-ui-filter-default={defaultValue}
      data-ui-filter-all={allValue}
      {...(hasOverflow
        ? {
            "data-ui-filter-visible-options":
              WIDGET_FILTER_INITIAL_VISIBLE_OPTIONS,
          }
        : {})}
    >
      {hasOverflow && (
        <OperatorChoiceTools
          className="widget-filter-tools"
          data-ui-filter-tools
          hidden
        >
          <OperatorChoiceSearch
            className="widget-filter-search"
            type="search"
            autoComplete="off"
            placeholder="Search filters"
            aria-label={`Search ${label}`}
            data-ui-filter-search
          />
          <OperatorChoiceToggle
            className="widget-filter-toggle"
            type="button"
            aria-expanded="false"
            data-ui-filter-toggle
          >
            <span data-ui-filter-toggle-label>Show all</span>
          </OperatorChoiceToggle>
        </OperatorChoiceTools>
      )}
      <OperatorChoiceGroup
        compact
        className="widget-filter-tabs widget-filter-tabs--compact"
        aria-label={label}
      >
        {options.map((option) => {
          const active = option.value === defaultValue;
          return (
            <OperatorChoiceButton
              compact
              {...option.triggerProps}
              key={option.value}
              className="widget-filter-tab"
              type="button"
              data-ui-filter-value={option.value}
              data-ui-filter-option-label={filterOptionSearchText(option)}
              aria-pressed={active ? "true" : "false"}
            >
              {option.count !== undefined && (
                <OperatorChoiceCount
                  className="widget-filter-count"
                  attention={option.tone === "gap"}
                >
                  {option.count}
                </OperatorChoiceCount>
              )}
              <OperatorChoiceLabel className="widget-filter-label">
                {option.label}
              </OperatorChoiceLabel>
            </OperatorChoiceButton>
          );
        })}
      </OperatorChoiceGroup>
      {children}
      {emptyState !== undefined && (
        <div data-ui-filter-empty hidden>
          {emptyState}
        </div>
      )}
    </div>
  );
}

export function WidgetList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <OperatorSummaryList className={className}>{children}</OperatorSummaryList>
  );
}

export function WidgetMetaLine({
  segments,
}: {
  segments: string[];
}): JSX.Element | null {
  if (segments.length === 0) return null;

  return (
    <OperatorSummaryMetadata>
      {segments.map((segment, index) => (
        <span key={`${segment}:${index}`}>
          {index > 0 && <OperatorSummarySeparator>·</OperatorSummarySeparator>}
          {segment}
        </span>
      ))}
    </OperatorSummaryMetadata>
  );
}

export function WidgetTags({ tags }: { tags: string[] }): JSX.Element | null {
  if (tags.length === 0) return null;

  return (
    <OperatorSummaryTags>
      {tags.map((tag, index) => (
        <OperatorSummaryTag key={`${tag}:${index}`}>{tag}</OperatorSummaryTag>
      ))}
    </OperatorSummaryTags>
  );
}

export function WidgetStatusPill({
  children,
  tone = "plain",
}: {
  children: ReactNode;
  tone?: "plain" | "warn" | "error" | "ok" | "muted";
}): JSX.Element {
  const statusTone = (
    {
      plain: "neutral",
      warn: "warn",
      error: "error",
      ok: "good",
      muted: "muted",
    } as const
  )[tone];
  return (
    <OperatorStatusPill presentation="label" tone={statusTone}>
      {children}
    </OperatorStatusPill>
  );
}

export function WidgetListItem({
  title,
  description,
  meta = [],
  tags = [],
  trailing,
  filterValues,
  className,
  itemProps,
}: {
  title: ReactNode;
  description?: ReactNode | undefined;
  meta?: string[] | undefined;
  tags?: string[] | undefined;
  trailing?: ReactNode | undefined;
  filterValues?: string[] | undefined;
  className?: string | undefined;
  itemProps?: WidgetElementProps<HTMLLIElement> | undefined;
}): JSX.Element {
  return (
    <OperatorSummaryItem
      {...itemProps}
      className={className}
      heading={title}
      description={description}
      metadata={<WidgetMetaLine segments={meta} />}
      tags={<WidgetTags tags={tags} />}
      trailing={trailing}
      {...(filterValues
        ? { "data-ui-filter-values": JSON.stringify(filterValues) }
        : {})}
    />
  );
}
